import React, { useMemo, useState } from 'react';
import { Frame, TopBar, PART_PALETTE } from './_layout';
import { getPartProgress } from './_progress';

// Three reading-section "extras" surfaced from practice data:
//
//   view='mistakes'   — every wrong answer the user has made, grouped by part
//   view='vocabulary' — notable words/phrases harvested from completed parts
//                       (cloze answers, sign meanings, etc.)
//   view='training'   — what each part actually tests, with the user's own
//                       score per part highlighted so weak areas pop out
//
// All three read the same localStorage progress that Part1-6 screens write
// after they submit. Pure presentational + light derivation; no AI.

const PART_INFO = {
  1: {
    name: 'Signs & Notices',
    skill: 'Quick comprehension of short, real-world text',
    focus: 'Public signs, warnings, instructions, short notes. Read the whole sign before picking — distractors paraphrase one detail wrongly.',
    drills: [
      'Read every shop / station sign you walk past today, then summarise it in one sentence.',
      'When you see a notice, predict the answer in your head before reading the options.',
    ],
  },
  2: {
    name: 'Matching',
    skill: 'Scanning + matching needs to descriptions',
    focus: 'A list of people each want something specific. Don\'t read every text in full — scan for the keyword that matches each need.',
    drills: [
      'Underline the key need word for each person before looking at the texts.',
      'If two texts seem to match, look for the disqualifying detail in the wrong one.',
    ],
  },
  3: {
    name: 'True / False',
    skill: 'Detail comprehension — fact vs. inference',
    focus: 'A statement is FALSE if the passage says the opposite OR doesn\'t support it. Don\'t guess based on outside knowledge.',
    drills: [
      'For every statement, point to the exact sentence in the passage that proves your answer.',
      'Watch for absolute words like "never", "always", "no" — they often flip the answer.',
    ],
  },
  4: {
    name: 'MCQ — Article',
    skill: 'Close reading of a longer passage',
    focus: 'Each question targets one paragraph. Find the paragraph first, then re-read it carefully — wrong options paraphrase a different paragraph.',
    drills: [
      'Read the question before re-reading the passage; it tells you what to look for.',
      'Eliminate two clearly-wrong options first; the toss-up between the last two is where the real test is.',
    ],
  },
  5: {
    name: 'MCQ Cloze',
    skill: 'Vocabulary in context — collocations and word choice',
    focus: 'All three options usually fit the grammar. Only one fits the meaning AND the typical word-pairing. Read the WHOLE sentence around the blank.',
    drills: [
      'Build a list of "verb + preposition" pairs (e.g. depend ON, listen TO).',
      'When two options fit, ask: which one would a native speaker actually say?',
    ],
  },
  6: {
    name: 'Open Cloze',
    skill: 'Grammar & function words — articles, prepositions, conjunctions',
    focus: 'These blanks always take a small "function" word (a, the, of, by, and, but, which…). Spelling and capitalisation must match expectations.',
    drills: [
      'Practise filling articles + prepositions in news headlines.',
      'When you don\'t know the answer, ask: is this blank linking two ideas (and / but / so) or pointing at something (the / a / this)?',
    ],
  },
};

export default function ReadingExtras({
  theme, isMobile, isDark, navigate, toggleTheme, accountControl,
  exam, view = 'mistakes', setToast,
}) {
  const partProgress = getPartProgress();
  const parts = useMemo(
    () => (exam?.reading_parts || []).slice().sort((a, b) => (a.part_number || 0) - (b.part_number || 0)),
    [exam],
  );

  const completedParts = parts.filter((p) => p && partProgress[String(p.part_number)]);

  const labelFor = (v) => v === 'mistakes' ? 'Mistakes' : v === 'vocabulary' ? 'Vocabulary' : 'Training guide';
  const subtitleFor = (v) => {
    if (v === 'mistakes') return 'Every question you got wrong, grouped by part.';
    if (v === 'vocabulary') return 'Words and phrases worth remembering from the parts you have practised.';
    return 'How each FET reading part is tested — see where to focus next.';
  };

  return (
    <Frame theme={theme} isMobile={isMobile}>
      <TopBar
        theme={theme}
        isDark={isDark}
        accountControl={accountControl}
        toggleTheme={toggleTheme}
        onBack={() => navigate('/?mode=reading-home')}
      />

      {/* Title + sub */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase', color: theme.muted, marginBottom: 4 }}>
          Practice extras
        </div>
        <h1 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 900, color: theme.text, margin: 0, letterSpacing: '-.5px' }}>
          {labelFor(view)}
        </h1>
        <div style={{ fontSize: 13, color: theme.body, marginTop: 4, lineHeight: 1.5 }}>{subtitleFor(view)}</div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
        {['mistakes', 'vocabulary', 'training'].map((v) => {
          const active = view === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => navigate(`/?mode=reading-${v}`)}
              style={{
                padding: '8px 14px', borderRadius: 999,
                background: active ? theme.text : theme.surface,
                color: active ? theme.surface : theme.body,
                border: `1px solid ${active ? theme.text : theme.border}`,
                fontWeight: 800, fontSize: 12, cursor: 'pointer',
                fontFamily: 'inherit', letterSpacing: '.04em',
              }}
            >
              {labelFor(v)}
            </button>
          );
        })}
      </div>

      {/* Body — empty-state if nothing practised yet */}
      {completedParts.length === 0 && view !== 'training' ? (
        <EmptyState theme={theme} isMobile={isMobile} navigate={navigate} view={view} />
      ) : view === 'mistakes' ? (
        <MistakesView completedParts={completedParts} partProgress={partProgress} theme={theme} isMobile={isMobile} />
      ) : view === 'vocabulary' ? (
        <VocabularyView completedParts={completedParts} theme={theme} isMobile={isMobile} />
      ) : (
        <TrainingView parts={parts} partProgress={partProgress} theme={theme} isMobile={isMobile} />
      )}
    </Frame>
  );
}

function EmptyState({ theme, isMobile, navigate, view }) {
  const msg = view === 'mistakes'
    ? "You haven't finished any reading parts yet, so there are no mistakes to review."
    : "You haven't finished any reading parts yet, so there is no vocabulary to harvest yet.";
  return (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.border}`,
      borderRadius: 18, padding: isMobile ? 18 : 24,
      textAlign: 'center', color: theme.body, lineHeight: 1.6,
    }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>📚</div>
      <div style={{ fontWeight: 800, color: theme.text, marginBottom: 6 }}>Nothing here yet</div>
      <div style={{ fontSize: 14, marginBottom: 16 }}>{msg}</div>
      <button
        type="button"
        onClick={() => navigate('/?mode=reading-home')}
        style={{
          height: 44, padding: '0 22px', borderRadius: 12, border: 'none',
          background: theme.ctaBg, color: theme.ctaText, fontWeight: 800,
          fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        Back to reading home
      </button>
    </div>
  );
}

function MistakesView({ completedParts, partProgress, theme, isMobile }) {
  const allMistakes = completedParts.flatMap((p) => {
    const prog = partProgress[String(p.part_number)] || {};
    return (prog.mistakes || []).map((m) => ({ ...m, partNumber: p.part_number, partName: p.type_name || p.name || `Part ${p.part_number}` }));
  });

  if (allMistakes.length === 0) {
    return (
      <div style={{
        background: theme.surface, border: `1px solid ${theme.border}`,
        borderRadius: 18, padding: isMobile ? 18 : 24, textAlign: 'center',
        color: theme.body, lineHeight: 1.6,
      }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🎯</div>
        <div style={{ fontWeight: 800, color: theme.text, marginBottom: 6 }}>No mistakes — well done!</div>
        <div style={{ fontSize: 14 }}>Across the {completedParts.length} part{completedParts.length === 1 ? '' : 's'} you have finished, you got every question right.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 13, color: theme.muted, marginBottom: 12 }}>
        <strong style={{ color: theme.text }}>{allMistakes.length}</strong> mistake{allMistakes.length === 1 ? '' : 's'} across {completedParts.length} part{completedParts.length === 1 ? '' : 's'}.
      </div>
      {completedParts.map((p) => {
        const prog = partProgress[String(p.part_number)] || {};
        const mistakes = prog.mistakes || [];
        if (mistakes.length === 0) return null;
        const palette = PART_PALETTE[p.part_number] || { fg: theme.accent, bg: theme.accentSoftBg };
        return (
          <div key={p.part_number} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ width: 28, height: 28, borderRadius: 8, background: palette.bg, color: palette.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 13 }}>{p.part_number}</span>
              <div style={{ fontSize: 13, fontWeight: 800, color: theme.text }}>{p.type_name || p.name || `Part ${p.part_number}`}</div>
              <div style={{ flex: 1 }} />
              <div style={{ fontSize: 12, color: theme.muted, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                {mistakes.length} wrong of {prog.max}
              </div>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {mistakes.map((m, i) => (
                <MistakeCard key={i} mistake={m} theme={theme} isMobile={isMobile} accent={palette.fg} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MistakeCard({ mistake, theme, isMobile, accent }) {
  const [showAr, setShowAr] = useState(false);
  return (
    <div style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderLeft: `4px solid ${accent}`,
      borderRadius: 14,
      padding: isMobile ? '12px 14px' : '14px 16px',
    }}>
      <div style={{ fontSize: isMobile ? 13 : 14, color: theme.text, fontWeight: 700, lineHeight: 1.5, marginBottom: 8 }}>
        {mistake.prompt}
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: '#FEF2F2', border: '1px solid rgba(239,68,68,.2)', borderRadius: 8 }}>
          <span style={{ width: 18, height: 18, borderRadius: 9, background: '#ef4444', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>✗</span>
          <div style={{ fontSize: 12, color: '#7F1D1D', flex: 1 }}>
            <span style={{ fontWeight: 800 }}>You answered:</span> {mistake.yourAnswer}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: '#ECFDF5', border: '1px solid rgba(34,197,94,.25)', borderRadius: 8 }}>
          <span style={{ width: 18, height: 18, borderRadius: 9, background: '#22c55e', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>✓</span>
          <div style={{ fontSize: 12, color: '#065F46', flex: 1 }}>
            <span style={{ fontWeight: 800 }}>Correct:</span> {mistake.correctAnswer}
          </div>
        </div>
        {mistake.why_en && (
          <div style={{ fontSize: 12, color: theme.body, lineHeight: 1.55, padding: '8px 10px', background: theme.surfaceSoft, borderRadius: 8 }}>
            <span style={{ fontWeight: 800, color: theme.text }}>Why:</span> {mistake.why_en}
          </div>
        )}
        {mistake.why_ar && (
          <button
            type="button"
            onClick={() => setShowAr((v) => !v)}
            style={{
              background: 'transparent', border: `1px dashed ${theme.border}`, padding: '6px 10px',
              borderRadius: 8, color: theme.muted, fontSize: 11, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
            }}
          >
            {showAr ? 'Hide Arabic ▲' : 'Show Arabic ▼'}
          </button>
        )}
        {showAr && mistake.why_ar && (
          <div lang="ar" dir="rtl" style={{ fontSize: 13, color: theme.body, fontFamily: "'Cairo', 'Tajawal', system-ui, sans-serif", textAlign: 'right', padding: '8px 10px', background: theme.surfaceSoft, borderRadius: 8 }}>
            {mistake.why_ar}
          </div>
        )}
      </div>
    </div>
  );
}

function VocabularyView({ completedParts, theme, isMobile }) {
  const items = harvestVocabulary(completedParts);

  if (items.length === 0) {
    return (
      <div style={{
        background: theme.surface, border: `1px solid ${theme.border}`,
        borderRadius: 18, padding: isMobile ? 18 : 24, textAlign: 'center',
        color: theme.body, lineHeight: 1.6,
      }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📖</div>
        <div style={{ fontWeight: 800, color: theme.text, marginBottom: 6 }}>No vocabulary harvested yet</div>
        <div style={{ fontSize: 14 }}>Finish a Part 1, 5 or 6 to start collecting words.</div>
      </div>
    );
  }

  // Group by part
  const groups = {};
  items.forEach((it) => {
    const k = it.partNumber;
    if (!groups[k]) groups[k] = [];
    groups[k].push(it);
  });

  return (
    <div>
      <div style={{ fontSize: 13, color: theme.muted, marginBottom: 12 }}>
        <strong style={{ color: theme.text }}>{items.length}</strong> term{items.length === 1 ? '' : 's'} from your practice.
      </div>
      {Object.keys(groups).sort((a, b) => Number(a) - Number(b)).map((pn) => {
        const palette = PART_PALETTE[Number(pn)] || { fg: theme.accent, bg: theme.accentSoftBg };
        return (
          <div key={pn} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ width: 28, height: 28, borderRadius: 8, background: palette.bg, color: palette.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 13 }}>{pn}</span>
              <div style={{ fontSize: 13, fontWeight: 800, color: theme.text }}>{groups[pn][0].partName}</div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {groups[pn].map((it, i) => (
                <div key={i} style={{
                  background: theme.surface, border: `1px solid ${theme.border}`,
                  borderRadius: 12, padding: '10px 12px',
                  minWidth: 140, flex: '1 1 200px', maxWidth: 320,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: theme.text }}>{it.term}</div>
                  {it.meaning && <div style={{ fontSize: 12, color: theme.body, marginTop: 4, lineHeight: 1.5 }}>{it.meaning}</div>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function harvestVocabulary(completedParts) {
  const items = [];
  const seen = new Set();
  const push = (term, partNumber, partName, meaning) => {
    const key = `${partNumber}:${(term || '').toLowerCase().trim()}`;
    if (!term || seen.has(key)) return;
    seen.add(key);
    items.push({ term: term.trim(), partNumber, partName, meaning });
  };

  completedParts.forEach((p) => {
    const partName = p.type_name || p.name || `Part ${p.part_number}`;
    const c = p.content || {};
    switch (p.part_number) {
      case 1: {
        // Sign text + the admin's plain-language explanation (why_en) is a great
        // gloss for the sign itself.
        (c.signs || c.questions || []).forEach((s) => {
          const term = (s.text || '').trim();
          if (term && term.length < 60) push(term, 1, partName, s.why_en || null);
        });
        break;
      }
      case 5: {
        // The CORRECT cloze word is what the student should remember.
        (c.blanks || []).forEach((b) => {
          const correct = String(b.answer || '').toUpperCase();
          const idx = ['A', 'B', 'C', 'D'].indexOf(correct);
          const term = (b.options || [])[idx];
          if (term) push(term, 5, partName, b.why_en || null);
        });
        break;
      }
      case 6: {
        (c.answers || []).forEach((a) => {
          const term = String(a || '').trim();
          if (term) push(term, 6, partName, null);
        });
        break;
      }
      default:
        // Parts 2/3/4 don't yield clean single-word vocab — skip.
        break;
    }
  });

  return items;
}

function TrainingView({ parts, partProgress, theme, isMobile }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {parts.map((p) => {
        const info = PART_INFO[p.part_number];
        if (!info) return null;
        const prog = partProgress[String(p.part_number)];
        const palette = PART_PALETTE[p.part_number] || { fg: theme.accent, bg: theme.accentSoftBg };
        const pct = prog?.max ? Math.round((prog.score / prog.max) * 100) : null;
        const tone = pct == null ? null : pct >= 80 ? '#22c55e' : pct >= 60 ? '#3b82f6' : '#f59e0b';
        const weak = pct != null && pct < 70;
        return (
          <div key={p.part_number} style={{
            background: theme.surface,
            border: `1px solid ${weak ? '#f59e0b' : theme.border}`,
            borderLeft: `4px solid ${palette.fg}`,
            borderRadius: 16,
            padding: isMobile ? '14px 14px' : '16px 18px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ width: 32, height: 32, borderRadius: 10, background: palette.bg, color: palette.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14 }}>{p.part_number}</span>
              <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 800, color: theme.text, letterSpacing: '-.2px' }}>{info.name}</div>
              <div style={{ flex: 1 }} />
              {prog ? (
                <span style={{ fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 999, background: tone === '#f59e0b' ? '#FFFBEB' : tone === '#3b82f6' ? '#EFF6FF' : '#ECFDF5', color: tone, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                  Your score · {prog.score}/{prog.max} · {pct}%
                </span>
              ) : (
                <span style={{ fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 999, background: theme.surfaceSoft, color: theme.muted, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                  Not started
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: palette.fg, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
              {info.skill}
            </div>
            <div style={{ fontSize: 13, color: theme.body, lineHeight: 1.6, marginBottom: 10 }}>{info.focus}</div>
            <div style={{ fontSize: 11, color: theme.muted, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
              {weak ? 'Suggested drills (focus area)' : 'Drills to practise'}
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, color: theme.body, fontSize: 13, lineHeight: 1.6 }}>
              {info.drills.map((d, i) => (<li key={i} style={{ marginBottom: 2 }}>{d}</li>))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
